import React, { useState, useEffect } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { ManualGameDeposit } from '../../../types';
import { useConfirmManualGameDeposit } from '../../../hooks/useApi';
import { LoadingSpinner } from '../../ui/Loading';
import { formatCurrency } from '../../../utils';
import { SUCCESS_MESSAGES } from '../../../constants';

interface ConfirmDepositModalProps {
  deposit: ManualGameDeposit | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const parseErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An error occurred while confirming deposit';
};

const ConfirmDepositModal: React.FC<ConfirmDepositModalProps> = ({
  deposit,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const existingAccount = deposit?.gameAccount;
  const [adminNotes, setAdminNotes] = useState('');
  const [gameUsername, setGameUsername] = useState('');
  const [gamePassword, setGamePassword] = useState('');
  const confirmMutation = useConfirmManualGameDeposit();

  useEffect(() => {
    if (existingAccount) {
      setGameUsername(existingAccount.gameUsername || '');
      setGamePassword(existingAccount.password || '');
    }
  }, [existingAccount]);

  const hasExistingAccount = !!existingAccount;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deposit) return;

    if (!hasExistingAccount) {
      if (!gameUsername.trim()) {
        toast.error('Game Username is required');
        return;
      }
      if (!gamePassword.trim()) {
        toast.error('Game Password is required');
        return;
      }
    }

    try {
      const data: Record<string, string> = {};
      if (gameUsername.trim()) data.gameUsername = gameUsername.trim();
      if (gamePassword.trim()) data.gamePassword = gamePassword.trim();
      if (adminNotes.trim()) data.adminNotes = adminNotes.trim();

      await confirmMutation.mutateAsync({
        transactionId: deposit.transactionId,
        data: Object.keys(data).length > 0 ? data : undefined,
      });
      toast.success(SUCCESS_MESSAGES.MANUAL_DEPOSIT_CONFIRMED);
      onSuccess?.();
      onClose();
      resetForm();
    } catch (error: any) {
      console.error('Confirm deposit error:', error);
      const errorMessage = parseErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  const resetForm = () => {
    setAdminNotes('');
    setGameUsername(existingAccount?.gameUsername || '');
    setGamePassword(existingAccount?.password || '');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen || !deposit) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold">Confirm Manual Deposit</CardTitle>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            {/* Deposit Summary */}
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Confirm that you have successfully deposited the following amount into the game:
              </p>
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Amount</span>
                    <span className="font-bold text-green-700 text-lg">
                      {formatCurrency(deposit.amount)} {deposit.currency || 'USD'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Game</span>
                    <span className="font-medium">{deposit.gameProvider.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">User</span>
                    <span className="font-medium">{deposit.userEmail}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Transaction</span>
                    <span className="font-mono text-xs">{deposit.transactionId}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Game Account Section */}
            <div className="space-y-3 border rounded-lg p-4">
              <h4 className="text-sm font-semibold">
                Game Account
                {!hasExistingAccount && <span className="text-red-600 ml-1">*</span>}
              </h4>
              <div>
                <label htmlFor="gameUsername" className="block text-sm font-medium mb-1">
                  Game Username {!hasExistingAccount && <span className="text-red-600">*</span>}
                </label>
                <Input
                  id="gameUsername"
                  name="game-user-field"
                  type="text"
                  placeholder="e.g., player123"
                  value={gameUsername}
                  onChange={(e) => setGameUsername(e.target.value)}
                  maxLength={100}
                  required={!hasExistingAccount}
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore="true"
                />
              </div>
              <div>
                <label htmlFor="gamePassword" className="block text-sm font-medium mb-1">
                  Game Password {!hasExistingAccount && <span className="text-red-600">*</span>}
                </label>
                <Input
                  id="gamePassword"
                  name="game-pwd-field"
                  type="text"
                  placeholder="e.g., secret456"
                  value={gamePassword}
                  onChange={(e) => setGamePassword(e.target.value)}
                  maxLength={100}
                  required={!hasExistingAccount}
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore="true"
                />
              </div>
            </div>

            {/* Admin Notes (optional) */}
            <div>
              <label htmlFor="adminNotes" className="block text-sm font-medium mb-2">
                Admin Notes <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <Input
                id="adminNotes"
                type="text"
                placeholder="e.g., Deposited via game admin panel"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {adminNotes.length}/500 characters
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={confirmMutation.isLoading || (!hasExistingAccount && (!gameUsername.trim() || !gamePassword.trim()))}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {confirmMutation.isLoading ? (
                  <div className="flex items-center">
                    <LoadingSpinner />
                    <span className="ml-2">Confirming...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Confirm Deposit
                  </div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfirmDepositModal;
